import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Dialog from '@material-ui/core/Dialog';
import InputBase from '@material-ui/core/InputBase';
import LinearProgress from '@material-ui/core/LinearProgress';
import SearchIcon from '@material-ui/icons/Search';
import ListAltOutlinedIcon from '@material-ui/icons/ListAltOutlined';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
import AddIcon from '@material-ui/icons/Add';
import api from '../services/api';
import { pode } from '../services/acesso';
import { CORES } from '../theme';

const semAcento = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const formatarCnpj = (d) => d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');

const Kbd = ({ children }) => (
    <kbd style={{ font: '600 11px Roboto, Arial, sans-serif', border: `1px solid ${CORES.borda}`, borderRadius: 4, padding: '1px 5px', background: '#fff', color: CORES.textoSuave }}>
        {children}
    </kbd>
);

/**
 * Busca global (Ctrl+K / Cmd+K): leads por razão social ou CNPJ e clientes/projetos por nome.
 * Usa só endpoints que já existem:
 *   - GET leads?corporate_name=…  (ou cnpj=… quando o texto tem 14 dígitos)
 *   - GET customers/select/get     (lista carregada uma vez e filtrada no navegador)
 */
const BuscaGlobal = () => {
    const history = useHistory();
    const [aberta, setAberta] = useState(false);
    const [texto, setTexto] = useState('');
    const [leads, setLeads] = useState([]);
    const [clientes, setClientes] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [ativo, setAtivo] = useState(0);
    const requisicao = useRef(0);

    // Atalho de teclado
    useEffect(() => {
        const aoTeclar = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setAberta(true);
            }
        };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, []);

    // Clientes/projetos: carrega uma vez, na primeira abertura
    useEffect(() => {
        if (!aberta || clientes !== null) return;
        // mesma regra da Home: sem visão de gestão, só os clientes da carteira do assessor
        const params = pode('visao_gestao') ? {} : { assessor_id: localStorage.getItem('id_assessor') };
        api.get('customers/select/get', { params })
            .then(({ data }) => setClientes(Array.isArray(data) ? data : []))
            .catch(() => setClientes([]));
    }, [aberta, clientes]);

    // Leads: busca com atraso de 300 ms enquanto digita
    useEffect(() => {
        const q = texto.trim();
        setAtivo(0);
        if (!aberta || q.length < 2 || !pode('visualizar')) {
            setLeads([]);
            return undefined;
        }
        const atual = ++requisicao.current;
        const t = setTimeout(() => {
            const digitos = q.replace(/\D/g, '');
            const params = digitos.length === 14 ? { cnpj: formatarCnpj(digitos) } : { corporate_name: q };
            setCarregando(true);
            api.get('leads', { params: { page: 1, pageSize: 6, ...params } })
                .then(({ data }) => { if (atual === requisicao.current) setLeads((data && data.data) || []); })
                .catch(() => { if (atual === requisicao.current) setLeads([]); })
                .finally(() => { if (atual === requisicao.current) setCarregando(false); });
        }, 300);
        return () => clearTimeout(t);
    }, [texto, aberta]);

    const q = semAcento(texto.trim());
    const clientesFiltrados = q.length >= 2 && clientes
        ? clientes.filter((c) => semAcento(c.name).includes(q)).slice(0, 5)
        : [];

    const itens = [
        ...leads.map((l) => ({ grupo: 'Leads', chave: 'l' + l.id, titulo: l.corporate_name, sub: l.cnpj ? formatarCnpj(String(l.cnpj).replace(/\D/g, '')) : '', icone: <ListAltOutlinedIcon fontSize="small" />, ir: '/leads/' + l.id + '/edit' })),
        ...clientesFiltrados.map((c) => ({ grupo: 'Clientes/projetos', chave: 'c' + c.id, titulo: c.name, sub: '', icone: <PeopleOutlinedIcon fontSize="small" />, ir: '/customer/' + c.id + '/edit' })),
        ...(texto.trim() && pode('editar_leads')
            ? [{ grupo: 'Ações', chave: 'novo', titulo: `Criar lead "${texto.trim()}"`, sub: '', icone: <AddIcon fontSize="small" />, ir: '/leads/novo' }]
            : []),
    ];

    const fechar = () => {
        setAberta(false);
        setTexto('');
        setLeads([]);
    };

    const abrirItem = (item) => {
        fechar();
        history.push(item.ir);
    };

    const aoTeclarNaBusca = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setAtivo((a) => Math.min(a + 1, itens.length - 1)); }
        if (e.key === 'ArrowUp') { e.preventDefault(); setAtivo((a) => Math.max(a - 1, 0)); }
        if (e.key === 'Enter' && itens[ativo]) { e.preventDefault(); abrirItem(itens[ativo]); }
    };

    let grupoAnterior = null;

    return (
        <>
            <button
                type="button"
                onClick={() => setAberta(true)}
                aria-label="Buscar (Ctrl+K)"
                style={{
                    display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,.15)', color: '#fff',
                    border: 0, borderRadius: 8, padding: '7px 12px', font: '400 14px Roboto, Arial, sans-serif',
                    cursor: 'pointer', minWidth: 0, maxWidth: 320, flex: '0 1 320px',
                }}
            >
                <SearchIcon fontSize="small" />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Buscar lead, CNPJ, cliente…</span>
                <span style={{ marginLeft: 'auto' }}><Kbd>Ctrl K</Kbd></span>
            </button>

            <Dialog open={aberta} onClose={fechar} fullWidth maxWidth="sm" PaperProps={{ style: { position: 'absolute', top: 80, margin: 0, borderRadius: 12 } }} aria-label="Busca global">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: `1px solid ${CORES.borda}` }}>
                    <SearchIcon style={{ color: CORES.textoSuave }} />
                    <InputBase
                        autoFocus
                        fullWidth
                        placeholder="Razão social, CNPJ ou cliente/projeto"
                        value={texto}
                        onChange={(e) => setTexto(e.target.value)}
                        onKeyDown={aoTeclarNaBusca}
                        inputProps={{ 'aria-label': 'Buscar' }}
                        style={{ fontSize: 18 }}
                    />
                    <Kbd>Esc</Kbd>
                </div>
                <div style={{ height: 3 }}>{carregando && <LinearProgress />}</div>
                <div role="listbox" style={{ maxHeight: 420, overflowY: 'auto', paddingBottom: 6 }}>
                    {texto.trim().length < 2 && (
                        <div style={{ padding: '16px 18px', fontSize: 14, color: CORES.textoSuave }}>Digite pelo menos 2 letras.</div>
                    )}
                    {texto.trim().length >= 2 && !carregando && itens.length === 0 && (
                        <div style={{ padding: '16px 18px', fontSize: 14, color: CORES.textoSuave }}>Nada encontrado.</div>
                    )}
                    {itens.map((item, i) => {
                        const cabecalho = item.grupo !== grupoAnterior ? item.grupo : null;
                        grupoAnterior = item.grupo;
                        return (
                            <React.Fragment key={item.chave}>
                                {cabecalho && (
                                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: CORES.textoSuave, padding: '12px 18px 6px' }}>
                                        {cabecalho}
                                    </div>
                                )}
                                <div
                                    role="option"
                                    aria-selected={i === ativo}
                                    tabIndex={-1}
                                    onMouseEnter={() => setAtivo(i)}
                                    onClick={() => abrirItem(item)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', fontSize: 14, cursor: 'pointer', background: i === ativo ? CORES.verdeClaro : undefined }}
                                >
                                    <span style={{ width: 32, height: 32, borderRadius: 8, background: '#EEF1EC', color: CORES.verdeEscuro, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                                        {item.icone}
                                    </span>
                                    <span style={{ minWidth: 0 }}>
                                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.titulo}</span>
                                        {item.sub && <small style={{ color: CORES.textoSuave, fontSize: 12 }}>{item.sub}</small>}
                                    </span>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
                <div style={{ display: 'flex', gap: 16, padding: '10px 18px', borderTop: `1px solid ${CORES.borda}`, fontSize: 12, color: CORES.textoSuave }}>
                    <span><Kbd>↑</Kbd> <Kbd>↓</Kbd> navegar</span>
                    <span><Kbd>Enter</Kbd> abrir</span>
                    <span><Kbd>Esc</Kbd> fechar</span>
                </div>
            </Dialog>
        </>
    );
};

export default BuscaGlobal;
